import { useState, useCallback, useRef, useEffect } from 'react';
import { fetchTranslation } from '../api/translationAPI.js';
import { trackEvent } from '../utils/ga.js';
import { isKorean, isEnglish } from '../utils/languageHelpers.js';
import { MAX_CHARS } from '../constants.js';

export const useTranslation = (settings, setSettings, provider, t, pageType) => {
    const { sourceLang } = settings;
    const [sourceText, setSourceText] = useState('');
    const [targetText, setTargetText] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');
    const [copySuccess, setCopySuccess] = useState(false);

    const [isCoolingDown, setIsCoolingDown] = useState(false);
    const [cooldownSeconds, setCooldownSeconds] = useState(0);
    const cooldownTimer = useRef(null);

    useEffect(() => {
        return () => clearInterval(cooldownTimer.current);
    }, []);

    const isCharLimitExceeded = sourceText.length > MAX_CHARS;

    const swapSpeakerListenerRoles = useCallback((currentSettings) => {
        const { speaker, listener } = currentSettings;
        return { ...currentSettings, speaker: listener, listener: speaker };
    }, []);

    const handleTranslate = useCallback(async () => {
        if (isLoading || isCoolingDown) return;

        if (isCharLimitExceeded) {
            setError(t('messages.charLimitExceeded'));
            trackEvent('translation_attempt_failed', { reason: 'char_limit_exceeded', char_length: sourceText.length });
            return;
        }

        if (!sourceText.trim()) {
            setTargetText('');
            setError('');
            return;
        }

        const targetLang = sourceLang === 'vi' ? pageType.split('-')[0] : 'vi';
        trackEvent('translate', { provider, direction: `${sourceLang}_to_${targetLang}`, char_length: sourceText.length, ...settings });

        setIsLoading(true);
        setError('');
        setTargetText('');

        setIsCoolingDown(true);
        setCooldownSeconds(7);
        cooldownTimer.current = setInterval(() => {
            setCooldownSeconds(prev => {
                if (prev <= 1) {
                    clearInterval(cooldownTimer.current);
                    setIsCoolingDown(false);
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);

        fetchTranslation({
            provider,
            sourceLang,
            sourceText,
            settings,
            languagePair: pageType
        })
            .then(translated => {
                setTargetText(translated);
            })
            .catch(err => {
                setError(err.message || "서버와 통신할 수 없습니다.");
                trackEvent('translation_failure', { provider, error_message: err.message });
            })
            .finally(() => {
                setIsLoading(false);
            });

    }, [sourceText, sourceLang, settings, provider, t, isCharLimitExceeded, isLoading, isCoolingDown, pageType]);

    const handleSwap = () => {
        const newLang = sourceLang === 'vi' ? pageType.split('-')[0] : 'vi';
        trackEvent('swap_language', { to_lang: newLang });

        setSettings(prev => {
            const roleSwappedSettings = swapSpeakerListenerRoles(prev);
            return { ...roleSwappedSettings, sourceLang: newLang };
        });

        setSourceText(targetText);
        setTargetText('');
        setError('');
    };

    const handleClear = () => {
        trackEvent('clear_text');
        setSourceText('');
        setTargetText('');
        setError('');
        if (window.speechSynthesis) {
            window.speechSynthesis.cancel();
        }
    };

    const handleSourceChange = useCallback((e) => {
        const newText = e.target.value;
        const textToSet = newText.substring(0, MAX_CHARS);
        setSourceText(textToSet);

        if (textToSet.trim().length === 0) return;

        if (pageType === 'ko-vn') {
            const detectedKorean = isKorean(textToSet);
            if (detectedKorean && sourceLang !== 'ko') {
                setSettings(prev => ({ ...swapSpeakerListenerRoles(prev), sourceLang: 'ko' }));
                trackEvent('auto_detect_language', { detected_lang: 'ko' });
            } else if (!detectedKorean && sourceLang !== 'vi') {
                setSettings(prev => ({ ...swapSpeakerListenerRoles(prev), sourceLang: 'vi' }));
                trackEvent('auto_detect_language', { detected_lang: 'vi' });
            }
        } else if (pageType === 'en-vn') {
            const detectedEnglish = isEnglish(textToSet);
            if (detectedEnglish && sourceLang !== 'en') {
                setSettings(prev => ({ ...swapSpeakerListenerRoles(prev), sourceLang: 'en' }));
                trackEvent('auto_detect_language', { detected_lang: 'en' });
            } else if (!detectedEnglish && sourceLang !== 'vi') {
                setSettings(prev => ({ ...swapSpeakerListenerRoles(prev), sourceLang: 'vi' }));
                trackEvent('auto_detect_language', { detected_lang: 'vi' });
            }
        }
    }, [sourceLang, setSettings, swapSpeakerListenerRoles, pageType]);

    const handleCopy = () => {
        if (!targetText) return;
        trackEvent('copy_text', { char_length: targetText.length });
        navigator.clipboard.writeText(targetText).then(() => {
            setCopySuccess(true);
            setTimeout(() => setCopySuccess(false), 2000);
        }, (err) => {
            console.error('복사 실패:', err);
        });
    };

    return {
        sourceLang,
        sourceText,
        targetText,
        isLoading,
        error,
        isCharLimitExceeded,
        handleSourceChange,
        handleTranslate,
        handleSwap,
        handleClear,
        handleCopy,
        copySuccess,
        isCoolingDown,
        cooldownSeconds,
    };
};
